import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDepartments } from "../../../hooks/useDepartments";
import { ongoingProjectService } from "../../../services/ongoingProjectService";
import { ongoingProjectSubmissionService } from "../../../services/ongoingProjectSubmissionService";
import { userService } from "../../../services/userService";
import LoadingSpinner from "../../common/LoadingSpinner";
import { EmptyState } from "../../common/EmptyState";
import Modal from "../../ui/Modal";
import Button from "../../ui/Button";
import DebouncedInput from "../../ui/DebouncedInput";
import { USER_ROLES } from "../../../constants/authConstants";
import {
  formatOngoingProjectAmount,
  DEFAULT_ONGOING_PROJECT,
  calculateBalanceRemaining,
  SUBMISSION_MESSAGES,
} from "../../../constants/ongoingProjectConstants";
import {
  financialYearUtils,
  debounce,
  dateUtils,
} from "../../../utils/helpers";
import { validateOngoingProject } from "../../../utils/ongoingProjectValidation";
import { exportOngoingProjectsPDF } from "../../../utils/pdfExport";

const OngoingProjectForm = () => {
  const { user, getUserRole, getUserDisplayName } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const {
    departments,
    loading: loadingDepartments,
    getDepartmentLabel,
    getDepartmentOptions,
  } = useDepartments();

  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  const isDepartment = getUserRole() === USER_ROLES.DEPARTMENT;

  // State management
  const [loading, setLoading] = useState(true);
  const [ongoingData, setOngoingData] = useState(null);
  const [originalOngoingData, setOriginalOngoingData] = useState(null);
  const [filteredOngoingData, setFilteredOngoingData] = useState(null);

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    financialYear: isDepartment
      ? financialYearUtils.getCurrentFinancialYear()
      : "",
    department: isAdmin ? "" : user?.department || "",
  });

  const [error, setError] = useState("");
  const [saving, setSaving] = useState({});
  const [errors, setErrors] = useState({});
  const [userCache, setUserCache] = useState({});

  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [exporting, setExporting] = useState(false);

  const canEdit =
    !loadingSubmission &&
    filters.financialYear &&
    (isAdmin || !submissionStatus?.isLocked);
  const isTableLocked = submissionStatus?.isLocked || false;

  // Build year options from current year down to 1990
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear; year >= 1990; year--) {
    yearOptions.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  const titleSize = getResponsiveValue({
    mobile: "text-xl",
    desktop: "text-2xl",
  });

  const cardPadding = getResponsiveValue({
    mobile: "p-4",
    desktop: "p-6",
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      financialYear: financialYearUtils.getCurrentFinancialYear(),
      department: isAdmin ? "" : user?.department || "",
    });
  };

  const hasActiveFilters =
    filters.search ||
    (filters.financialYear &&
      filters.financialYear !== financialYearUtils.getCurrentFinancialYear()) ||
    (isAdmin && filters.department);

  const getUserDisplayNameFromId = async (userId) => {
    if (!userId) return "System";
    if (userCache[userId]) return userCache[userId];

    try {
      const userData = await userService.getUserById(userId);
      const displayName = userData
        ? userData.fullName || userData.email || "Unknown User"
        : "Unknown User";
      setUserCache((prev) => ({ ...prev, [userId]: displayName }));
      return displayName;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return "Unknown User";
    }
  };

  const loadUserData = async (projects) => {
    const userIds = [
      ...new Set(
        projects.map((p) => p.updatedBy || p.createdBy).filter(Boolean),
      ),
    ];
    for (const userId of userIds) {
      if (!userCache[userId]) {
        await getUserDisplayNameFromId(userId);
      }
    }
  };

  const loadSubmissionStatus = async () => {
    if (!filters.financialYear) {
      setLoadingSubmission(false);
      setSubmissionStatus(null);
      return;
    }

    try {
      setLoadingSubmission(true);
      const department = isAdmin ? filters.department : user.department;

      if (!department) {
        setSubmissionStatus(null);
        setLoadingSubmission(false);
        return;
      }

      const result = await ongoingProjectSubmissionService.getSubmissionStatus(
        department,
        filters.financialYear,
      );

      setSubmissionStatus(result.data);
    } catch (error) {
      console.error("Error loading submission status:", error);
      setSubmissionStatus(null);
    } finally {
      setLoadingSubmission(false);
    }
  };

  const debouncedAutoSave = useCallback(
    debounce(async (projectId, projectData) => {
      if (isTableLocked && !isAdmin) return;

      try {
        setSaving((prev) => ({ ...prev, [projectId]: true }));
        await ongoingProjectService.updateOngoingProject(
          projectId,
          projectData,
          user.uid,
        );
        setSaving((prev) => ({ ...prev, [projectId]: false }));

        if (!userCache[user.uid]) {
          await getUserDisplayNameFromId(user.uid);
        }
      } catch (error) {
        console.error("Auto-save error:", error);
        setSaving((prev) => ({ ...prev, [projectId]: false }));
      }
    }, 1500),
    [user.uid, userCache, isTableLocked, isAdmin],
  );

  const applyFilters = () => {
    if (!ongoingData) {
      setFilteredOngoingData(null);
      return;
    }

    let filtered = [...ongoingData];

    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(
        (project) =>
          project.projectName?.toLowerCase().includes(searchLower) ||
          project.department?.toLowerCase().includes(searchLower),
      );
    }

    setFilteredOngoingData(filtered);
  };

  const loadOngoingData = async () => {
    try {
      setLoading(true);
      setError("");

      let result;

      if (isAdmin) {
        if (filters.department) {
          result = await ongoingProjectService.getOngoingProjectsByDepartment(
            filters.department,
            filters.financialYear,
          );
        } else {
          result = await ongoingProjectService.getOngoingProjects(
            filters.financialYear,
            null,
          );
        }
      } else {
        result = await ongoingProjectService.getOngoingProjectsByDepartment(
          user.department,
          filters.financialYear,
        );
      }

      if (result.success) {
        setOriginalOngoingData(result.data);
        setOngoingData(result.data);
        setFilteredOngoingData(result.data);
        await loadUserData(result.data);
      }
    } catch (error) {
      console.error("Error loading ongoing data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = async () => {
    if (!filters.financialYear) {
      alert("Please select a financial year first.");
      return;
    }

    if (isTableLocked && !isAdmin) {
      alert(SUBMISSION_MESSAGES.LOCKED_EDIT_WARNING);
      return;
    }

    try {
      const financialYear =
        filters.financialYear || financialYearUtils.getCurrentFinancialYear();

      const newProject = {
        ...DEFAULT_ONGOING_PROJECT,
        financialYear,
        department: isAdmin ? filters.department || "" : user.department,
        projectName: `New Ongoing Project ${(originalOngoingData?.length || 0) + 1}`,
        yearOfCommencement: new Date().getFullYear(),
        totalEstimatedCost: 0,
        totalExpenditure: 0,
        balanceRemaining: 0,
      };

      const result = await ongoingProjectService.addOngoingProject(
        newProject,
        user.uid,
      );
      if (result.success) {
        const updatedOriginalData = [
          result.data,
          ...(originalOngoingData || []),
        ];
        setOriginalOngoingData(updatedOriginalData);
        setOngoingData(updatedOriginalData);
        setFilteredOngoingData(updatedOriginalData);

        setSuccess(true);
        setSuccessMessage("New ongoing project added successfully!");
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error in handleAddNew:", error);
      setError("Failed to add new project: " + error.message);
    }
  };

  const handleDelete = async (projectId) => {
    if (!filters.financialYear) {
      alert("Please select a financial year first.");
      return;
    }

    if (isTableLocked && !isAdmin) {
      alert(SUBMISSION_MESSAGES.LOCKED_EDIT_WARNING);
      return;
    }

    if (!confirm("Are you sure you want to delete this project?")) return;

    try {
      await ongoingProjectService.deleteOngoingProject(projectId);

      const updatedOriginalData = originalOngoingData.filter(
        (p) => p.id !== projectId,
      );
      setOriginalOngoingData(updatedOriginalData);
      setOngoingData(updatedOriginalData);
      setFilteredOngoingData(updatedOriginalData);

      setSuccess(true);
      setSuccessMessage("Project deleted successfully!");
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      setError("Failed to delete project");
    }
  };

  const handleFieldChange = (projectId, field, value) => {
    if (isTableLocked && !isAdmin) {
      alert(SUBMISSION_MESSAGES.LOCKED_EDIT_WARNING);
      return;
    }

    setOngoingData((prev) => {
      return prev.map((project) => {
        if (project.id === projectId) {
          const updatedProject = { ...project, [field]: value };
          if (field === "totalEstimatedCost" || field === "totalExpenditure") {
            updatedProject.balanceRemaining = calculateBalanceRemaining(
              field === "totalEstimatedCost"
                ? value
                : project.totalEstimatedCost,
              field === "totalExpenditure" ? value : project.totalExpenditure,
            );
          }
          return updatedProject;
        }
        return project;
      });
    });

    setOriginalOngoingData((prev) =>
      prev.map((project) => {
        if (project.id === projectId) {
          const updatedProject = { ...project, [field]: value };
          if (field === "totalEstimatedCost" || field === "totalExpenditure") {
            updatedProject.balanceRemaining = calculateBalanceRemaining(
              field === "totalEstimatedCost"
                ? value
                : project.totalEstimatedCost,
              field === "totalExpenditure" ? value : project.totalExpenditure,
            );
          }
          return updatedProject;
        }
        return project;
      }),
    );

    setErrors((prev) => ({ ...prev, [`${projectId}.${field}`]: "" }));

    const project = ongoingData.find((p) => p.id === projectId);
    if (!project) return;

    const updatedProjectData = { ...project, [field]: value };
    const validation = validateOngoingProject(updatedProjectData);

    if (!validation.isValid && validation.errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [`${projectId}.${field}`]: validation.errors[field],
      }));
      return;
    }

    if (value !== undefined && value !== null && value !== "") {
      const currentData = { ...project, [field]: value };
      if (field === "totalEstimatedCost" || field === "totalExpenditure") {
        currentData.balanceRemaining = calculateBalanceRemaining(
          field === "totalEstimatedCost" ? value : project.totalEstimatedCost,
          field === "totalExpenditure" ? value : project.totalExpenditure,
        );
      }
      debouncedAutoSave(projectId, currentData);
    }
  };

  const handleSubmit = () => {
    if (!filteredOngoingData || filteredOngoingData.length === 0) {
      alert(SUBMISSION_MESSAGES.NO_PROJECTS);
      return;
    }
    setShowSubmitModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setSubmitting(true);
      const department = isAdmin ? filters.department : user.department;

      const result =
        await ongoingProjectSubmissionService.submitOngoingProjects(
          department,
          filters.financialYear,
          user.uid,
          getUserDisplayName(),
        );

      if (result.success) {
        setSuccess(true);
        setSuccessMessage(SUBMISSION_MESSAGES.SUBMIT_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage("");
        }, 5000);

        setShowSubmitModal(false);
        await loadSubmissionStatus();
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlock = async () => {
    if (!isAdmin) return;

    if (!confirm(SUBMISSION_MESSAGES.UNLOCK_CONFIRM)) return;

    try {
      const department = filters.department;

      const result =
        await ongoingProjectSubmissionService.unlockOngoingProjects(
          department,
          filters.financialYear,
          user.uid,
          getUserDisplayName(),
        );

      if (result.success) {
        setSuccess(true);
        setSuccessMessage(SUBMISSION_MESSAGES.UNLOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage("");
        }, 3000);

        await loadSubmissionStatus();
      }
    } catch (error) {
      console.error("Unlock error:", error);
      alert(error.message);
    }
  };

  const handleExport = () => {
    try {
      setExporting(true);

      const departmentName = isAdmin
        ? filters.department
          ? getDepartmentLabel(filters.department)
          : null
        : getDepartmentLabel(user.department);

      exportOngoingProjectsPDF(
        filteredOngoingData || [],
        departmentName,
        filters,
      );

      setSuccess(true);
      setSuccessMessage("PDF exported successfully!");
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setError("Failed to export PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const loadAllData = async () => {
      await loadSubmissionStatus();
      await loadOngoingData();
    };

    if (filters.financialYear && departments.length > 0) {
      loadAllData();
    } else {
      setLoading(false);
      setLoadingSubmission(false);
    }
  }, [filters.financialYear, filters.department, departments]);

  useEffect(() => {
    applyFilters();
  }, [ongoingData, filters.search]);

  if (loading || loadingDepartments || loadingSubmission) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading ongoing projects..." />
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm mb-6">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="ml-2 text-green-700 font-medium text-sm">
              {successMessage}
            </span>
          </div>
        </div>
      )}

      {/* Submission Status Banner */}
      {isDepartment && !loadingSubmission && submissionStatus && (
        <div
          className={`rounded-xl p-4 mb-6 ${submissionStatus.isLocked ? "bg-green-50 border border-green-200" : "bg-blue-50 border border-blue-200"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {submissionStatus.isLocked ? (
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                </svg>
              )}
              <div>
                {submissionStatus.isLocked ? (
                  <>
                    <p className="text-sm font-medium text-green-800">
                      Ongoing Projects Locked & Submitted
                    </p>
                    <p className="text-xs text-green-600">
                      Submitted on{" "}
                      {dateUtils.formatDate(submissionStatus.submittedAt)} by{" "}
                      {submissionStatus.submittedByName}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-blue-800">
                      Draft - Not Yet Submitted
                    </p>
                    <p className="text-xs text-blue-600">
                      Your ongoing projects data is saved but not locked.
                      Remember to submit when ready.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Locked Warning Banner */}
      {isDepartment && isTableLocked && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-orange-600 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-800">
                Submission Locked - Read Only Mode
              </p>
              <p className="text-xs text-orange-600 mt-1">
                Your ongoing projects data is locked. You cannot add, edit, or
                delete projects.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-2`}>
            Ongoing Projects
          </h1>
          <p className="text-text-secondary">
            {isAdmin
              ? "Manage ongoing projects across all departments"
              : "Manage ongoing projects for your department"}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleExport}
            size="md"
            variant="outline"
            className="flex items-center space-x-2"
            disabled={
              exporting ||
              !filteredOngoingData ||
              filteredOngoingData.length === 0
            }
            loading={exporting}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>{exporting ? "Exporting..." : "Export PDF"}</span>
          </Button>

          {isDepartment && !isTableLocked && (
            <Button
              onClick={handleSubmit}
              size="md"
              className="bg-green-600 hover:bg-green-700 flex items-center space-x-2"
              disabled={
                !filteredOngoingData || filteredOngoingData.length === 0
              }
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Lock & Submit</span>
            </Button>
          )}

          {isAdmin && isTableLocked && filters.department && (
            <Button
              onClick={handleUnlock}
              size="md"
              variant="outline"
              className="flex items-center space-x-2 border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                />
              </svg>
              <span>Unlock for Department</span>
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-error mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="mb-6 bg-surface-elevated rounded-xl border border-border-primary shadow-theme-sm">
        <div className={cardPadding}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              {/* Department Filter */}
              {isAdmin ? (
                <div className="min-w-0">
                  <label className="text-sm font-medium text-text-primary mb-1 block">
                    Department
                  </label>
                  <select
                    value={filters.department || ""}
                    onChange={(e) =>
                      handleFilterChange("department", e.target.value)
                    }
                    className="border border-border-primary rounded-xl bg-surface-primary
                      focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
                      text-text-primary appearance-none cursor-pointer
                      transition-colors duration-200 px-3 py-2 text-sm h-[38px] min-w-[180px]"
                  >
                    <option value="">All Departments</option>
                    {getDepartmentOptions().map((dept) => (
                      <option key={dept.value} value={dept.value}>
                        {dept.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="min-w-0">
                  <label className="text-sm font-medium text-text-primary mb-1 block">
                    Department
                  </label>
                  <div className="px-3 py-2 bg-surface-secondary rounded-xl border border-border-primary text-text-primary text-sm h-[38px] flex items-center whitespace-nowrap">
                    {getDepartmentLabel(user.department)}
                  </div>
                </div>
              )}

              {/* Search Filter */}
              <div className="flex-1 min-w-0">
                <label className="text-sm font-medium text-text-primary mb-1 block">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.search || ""}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="w-full border border-border-primary rounded-xl bg-surface-primary
                    focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
                    text-text-primary placeholder:text-text-tertiary
                    transition-colors duration-200 px-3 py-2 text-sm h-[38px]"
                  placeholder="Search projects..."
                />
              </div>

              {/* Financial Year Filter */}
              <div className="min-w-0">
                <label className="text-sm font-medium text-text-primary mb-1 block">
                  Financial Year <span className="text-error">*</span>
                </label>
                <select
                  value={filters.financialYear || ""}
                  onChange={(e) =>
                    handleFilterChange("financialYear", e.target.value)
                  }
                  className="border border-border-primary rounded-xl bg-surface-primary
                    focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
                    text-text-primary appearance-none cursor-pointer
                    transition-colors duration-200 px-3 py-2 text-sm h-[38px] min-w-[140px]"
                >
                  <option value="">Select Financial Year</option>
                  {financialYearUtils
                    .getFinancialYearOptions()
                    .map((option, index) => {
                      const uniqueKey =
                        option.value !== ""
                          ? option.value
                          : `fy-option-${index}`;
                      return (
                        <option key={uniqueKey} value={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                </select>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {filters.search && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Search: "{filters.search}"
                    <button
                      onClick={() => handleFilterChange("search", "")}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                )}
                {isAdmin && filters.department && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Department: "{getDepartmentLabel(filters.department)}"
                    <button
                      onClick={() => handleFilterChange("department", "")}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                )}
                {filters.financialYear &&
                  filters.financialYear !==
                    financialYearUtils.getCurrentFinancialYear() && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Financial Year: "
                      {financialYearUtils
                        .getFinancialYearOptions()
                        .find((fy) => fy.value === filters.financialYear)
                        ?.label || filters.financialYear}
                      "
                      <button
                        onClick={() =>
                          handleFilterChange(
                            "financialYear",
                            financialYearUtils.getCurrentFinancialYear(),
                          )
                        }
                        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  )}
              </div>
              <button
                onClick={clearFilters}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter Results Info */}
      {hasActiveFilters && filteredOngoingData && ongoingData && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-blue-500 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-blue-700 text-sm">
              Showing{" "}
              <span className="font-semibold">
                {filteredOngoingData.length}
              </span>{" "}
              of <span className="font-semibold">{ongoingData.length}</span>{" "}
              projects
              {filters.search && ` matching "${filters.search}"`}
            </span>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {!filteredOngoingData || filteredOngoingData.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg
                  className="w-16 h-16 text-text-quaternary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              }
              title={
                !filters.financialYear
                  ? "Please select a financial year"
                  : hasActiveFilters
                    ? "No projects match your filters"
                    : "No ongoing projects found"
              }
              description={
                !filters.financialYear
                  ? "Select a financial year from the dropdown above to view or add projects"
                  : hasActiveFilters
                    ? "Try adjusting your search or filter criteria to see more projects"
                    : isAdmin
                      ? `No ongoing projects found for ${filters.financialYear || "selected period"}`
                      : `No ongoing projects found for ${getDepartmentLabel(user.department)} in ${filters.financialYear || "selected period"}`
              }
              actionLabel={
                !filters.financialYear || !canEdit
                  ? null
                  : hasActiveFilters
                    ? "Clear Filters"
                    : "Add First Project"
              }
              onAction={
                !filters.financialYear || !canEdit
                  ? null
                  : hasActiveFilters
                    ? clearFilters
                    : handleAddNew
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S. No.
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">
                    Department
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">
                    Project Name
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Year of Commencement
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Total Approved Cost
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Total Clearance
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (including CFY)
                    </span>
                  </th>
                  <th
                    className={`px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-32 ${isAdmin ? "border-r border-gray-200" : ""}`}
                  >
                    Balance Remaining
                    <br />
                    <span className="text-gray-500 normal-case font-normal">
                      (in lakh)
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">
                    Remarks
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {/* Add New Row */}
                {canEdit && (
                  <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200">
                    <td className="px-3 py-3 text-center border-r border-gray-200">
                      <button
                        onClick={handleAddNew}
                        className="group flex items-center justify-center w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-200 transform hover:scale-110 shadow-lg hover:shadow-xl"
                        title="Add New Project"
                      >
                        <svg
                          className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-xs text-blue-600 font-medium border-r border-gray-200">
                      Click + to add new project
                    </td>
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                    <td className="px-3 py-3"></td>
                  </tr>
                )}

                {/* Project Rows */}
                {filteredOngoingData.map((project, index) => {
                  const isProjectSaving = saving[project.id];

                  return (
                    <tr
                      key={project.id}
                      className="hover:bg-blue-50 transition-colors duration-200"
                    >
                      {/* Serial Number */}
                      <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                        <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                          {index + 1}
                        </div>
                      </td>

                      {/* Department Cell */}
                      <td className="px-3 py-3 border-r border-gray-200">
                        {isAdmin ? (
                          <div>
                            <select
                              value={project.department || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  project.id,
                                  "department",
                                  e.target.value,
                                )
                              }
                              disabled={!canEdit}
                              className={`w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors[`${project.id}.department`] ? "border-red-500 focus:ring-red-500" : ""} ${!canEdit ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                            >
                              <option value="">Select Dept.</option>
                              {getDepartmentOptions().map((dept) => (
                                <option key={dept.value} value={dept.value}>
                                  {dept.label}
                                </option>
                              ))}
                            </select>
                            {errors[`${project.id}.department`] && (
                              <div className="text-xs text-red-600 mt-0.5 font-medium">
                                {errors[`${project.id}.department`]}
                              </div>
                            )}
                            {isProjectSaving && (
                              <div className="flex items-center mt-1">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                <span className="ml-1 text-xs text-blue-600">
                                  Saving...
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div className="text-xs text-gray-900 font-medium text-center">
                              {getDepartmentLabel(project.department)}
                            </div>
                            {isProjectSaving && (
                              <div className="flex items-center justify-center mt-1">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                <span className="ml-1 text-xs text-blue-600">
                                  Saving...
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Project Name Cell */}
                      <td className="px-3 py-3 border-r border-gray-200">
                        <DebouncedInput
                          type="text"
                          value={project.projectName || ""}
                          onChange={(value) =>
                            handleFieldChange(project.id, "projectName", value)
                          }
                          disabled={!canEdit}
                          delay={1200}
                          className={`w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors[`${project.id}.projectName`] ? "border-red-500 focus:ring-red-500" : ""} ${!canEdit ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                          placeholder="Enter project name"
                        />
                        {errors[`${project.id}.projectName`] && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">
                            {errors[`${project.id}.projectName`]}
                          </div>
                        )}
                      </td>

                      {/* Year of Commencement Cell */}
                      <td className="px-3 py-3 text-center border-r border-gray-200">
                        <select
                          value={project.yearOfCommencement || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              project.id,
                              "yearOfCommencement",
                              e.target.value,
                            )
                          }
                          disabled={!canEdit}
                          className={`w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors[`${project.id}.yearOfCommencement`] ? "border-red-500 focus:ring-red-500" : ""} ${!canEdit ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                        >
                          <option value="">Select Year</option>
                          {yearOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors[`${project.id}.yearOfCommencement`] && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">
                            {errors[`${project.id}.yearOfCommencement`]}
                          </div>
                        )}
                      </td>

                      {/* Total Approved Cost Cell */}
                      <td className="px-3 py-3 text-center border-r border-gray-200">
                        <DebouncedInput
                          type="number"
                          value={project.totalEstimatedCost || ""}
                          onChange={(value) =>
                            handleFieldChange(
                              project.id,
                              "totalEstimatedCost",
                              value,
                            )
                          }
                          disabled={!canEdit}
                          delay={1200}
                          className={`w-full text-xs text-center border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors[`${project.id}.totalEstimatedCost`] ? "border-red-500 focus:ring-red-500" : ""} ${!canEdit ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                          placeholder="0"
                        />
                        {errors[`${project.id}.totalEstimatedCost`] && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">
                            {errors[`${project.id}.totalEstimatedCost`]}
                          </div>
                        )}
                      </td>

                      {/* Total Clearance Cell */}
                      <td className="px-3 py-3 text-center border-r border-gray-200">
                        <DebouncedInput
                          type="number"
                          value={project.totalExpenditure || ""}
                          onChange={(value) =>
                            handleFieldChange(
                              project.id,
                              "totalExpenditure",
                              value,
                            )
                          }
                          disabled={!canEdit}
                          delay={1200}
                          className={`w-full text-xs text-center border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors[`${project.id}.totalExpenditure`] ? "border-red-500 focus:ring-red-500" : ""} ${!canEdit ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                          placeholder="0"
                        />
                        {errors[`${project.id}.totalExpenditure`] && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">
                            {errors[`${project.id}.totalExpenditure`]}
                          </div>
                        )}
                      </td>

                      {/* Balance Remaining Cell (Read-only) */}
                      <td className="px-3 py-3 text-center border-r border-gray-200">
                        <div className="w-full text-xs text-center px-2 py-1 bg-gray-100 rounded-lg text-gray-700 font-medium">
                          {formatOngoingProjectAmount(project.balanceRemaining)}
                        </div>
                      </td>

                      {/* Remarks Cell */}
                      <td className="px-3 py-3 border-r border-gray-200">
                        <textarea
                          value={project.remarks || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              project.id,
                              "remarks",
                              e.target.value,
                            )
                          }
                          disabled={!canEdit}
                          rows={2}
                          className={`w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${errors[`${project.id}.remarks`] ? "border-red-500 focus:ring-red-500" : ""} ${!canEdit ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                          placeholder="Add remarks..."
                          maxLength={500}
                        />
                        {errors[`${project.id}.remarks`] && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">
                            {errors[`${project.id}.remarks`]}
                          </div>
                        )}
                      </td>

                      {/* Actions Cell */}
                      <td className="px-3 py-3 text-center">
                        {canEdit ? (
                          <button
                            onClick={() => handleDelete(project.id)}
                            disabled={isProjectSaving}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete project"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        ) : (
                          <div
                            className="p-1.5 text-gray-400"
                            title="Locked - Contact admin to unlock"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* Total Row */}
                {filteredOngoingData && filteredOngoingData.length > 0 && (
                  <tr className="bg-gray-800 text-white font-bold border-t-4 border-gray-700">
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 text-xs font-bold border-r border-gray-700">
                      Total{hasActiveFilters && " (Filtered)"}
                    </td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatOngoingProjectAmount(
                        filteredOngoingData.reduce(
                          (sum, p) =>
                            sum + (parseFloat(p.totalEstimatedCost) || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatOngoingProjectAmount(
                        filteredOngoingData.reduce(
                          (sum, p) =>
                            sum + (parseFloat(p.totalExpenditure) || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatOngoingProjectAmount(
                        filteredOngoingData.reduce(
                          (sum, p) =>
                            sum + (parseFloat(p.balanceRemaining) || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submit Confirmation Modal */}
      <Modal
        isOpen={showSubmitModal}
        onClose={() => !submitting && setShowSubmitModal(false)}
        title="Lock & Submit Ongoing Projects"
        size="md"
      >
        <div className="p-6 space-y-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 text-orange-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Confirm Submission
              </h3>
              <p className="text-sm text-gray-600">
                {SUBMISSION_MESSAGES.SUBMIT_CONFIRM}
              </p>
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> You have{" "}
                  <strong>{filteredOngoingData?.length || 0} projects</strong>{" "}
                  in the current table. Make sure all data is correct before
                  submitting.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-border-primary">
            <Button
              variant="outline"
              onClick={() => setShowSubmitModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              loading={submitting}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? "Submitting..." : "Lock & Submit"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OngoingProjectForm;